import { useQuery } from "@tanstack/react-query";
import PageAPI from "../../../api/api/PageAPI";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { useAtomValue } from "jotai";
import useFieldMap from "../../../hooks/useFieldMap";
import { fillVariables } from "../../../api/api-hooks/records/useQueriedRecords";
import useTenantUsers from "../../../api/api-hooks/tenantUsers/useTenantUsers";
import useUser from "../../../api/api-hooks/auth/useUser";

interface Props {
  moduleAPIName: string | undefined;
  record: Record<string, any> | undefined;
  pageID: string | undefined;
  parentViewID: string | undefined;
  viewID: string | undefined;
  viewFilters: any;
  nodeDisplayFields: string[] | undefined;
  parentField: string | undefined;
}

// Hook for on demand hierarchy Flow Chart Generation.
// Necessary for SubViews - As there potentially hundreds of thousands of records
// And SubViews often use Records directly in their queries.
export default function useSubviewFlow({
  moduleAPIName,
  record,
  pageID,
  parentViewID,
  viewID,
  viewFilters,
  nodeDisplayFields,
  parentField,
}: Props) {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const { tenantUsersByRole, usersLoading } = useTenantUsers();
  const { user, userLoading } = useUser();

  const flow = useQuery({
    queryKey: ["SubViewFlow", moduleAPIName, viewID, record?._id, viewFilters],
    queryFn: () => {
      if (!viewFilters) {
        return Promise.reject(
          "View filters are required for Hierarchy Flow Charts inside SubViews. Go to this View's Settings and add a filter."
        );
      }

      const variableValues = {
        workspace: tenantUsersByRole,
        user: user,
        record: record,
      };

      const finalQuery = fillVariables(
        structuredClone(viewFilters), // REQUIRED CLONE - Major bugs if this isn't deep cloned.
        variableValues,
        fieldTypeMap
      );

      return PageAPI.generateFlowChartOnDemand(
        workspaceID,
        pageID,
        parentViewID,
        viewID,
        {
          ModuleAPIName: moduleAPIName,
          ParentField: parentField,
          NodeDisplayFields: nodeDisplayFields,
          TopLevelFilter: finalQuery?.Filters,
        }
      ).then((data) => {
        if (data.data.data.error) {
          return { Error: data.data.data.error };
        }

        return data.data.data.flow;
      });
    },
    enabled:
      !!viewID &&
      !!record?._id &&
      !!moduleAPIName &&
      !!parentViewID &&
      !!parentField &&
      !usersLoading &&
      !userLoading,
  });

  return {
    flow: flow.data,
    flowLoading: flow.isLoading,
    flowError: flow.error,
  };
}
